import React, { useState, useEffect } from 'react';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import { FormInitialValues } from 'src/components/CustomisationPage/pages/Configure';
import { useQuery } from '@apollo/client';
import { bundleProductDetail, bundleProductDetailVariables } from 'src/queries/__generated__/bundleProductDetail';
import { useCurrencyChange } from 'src/components/AppShell/ConfigProvider';
import { ErrorComponent } from 'src/components/Error/ErrorComponent';
import '../../scss/_root-component.scss';
import { BundleProductPage, ModalBundleProductPage } from 'src/components/BundleProduct/BundleProductPage';
import { NotFound } from 'src/components/NotFound/NotFound';
import { useSetResolved } from 'src/core/engine/router/index';
import { ProductPageSkeleton, BundlePageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { useDeps } from 'src/hooks/useDeps';
import { absoluteUrl } from 'src/util/absoluteUrl';
import { currentUrl } from 'src/util/currentUrl';
import { cleanBundleProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { TreatmentWithConfig } from 'src/types/global-types';
import { useBundleCustomisationData } from 'src/components/BundleProduct/hooks/useBundleCustomisationData';
import Bugsnag from '@bugsnag/js';
import { BaseProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { useIndexSku } from 'src/hooks/useIndexSku';
import { KEYWORDS } from 'src/constants/meta';
import { useKlaviyoViewedBundle } from 'src/features/klaviyo/hooks/useKlaviyoViewedBundle';
import { useBundleOptionsPrice } from 'src/hooks/useBundleOptionsPrice';
import { useSetPopupPageProduct } from 'src/components/PopupBanner/PopupPageContext';

import bundleProductQuery from '../../queries/bundleProductDetail.graphql';
import { Container, Wrapper } from '../../components/Layout';

type Props = {
    pathname: string;
    id: number;
    treatmentWithConfig: TreatmentWithConfig;
};

type BundleProductProps = {
    product: BaseProduct;
    customisationOption: string;
};

export const BundleProductRoot: React.FC<Props> = (props) => {
    useSetResolved();
    /**
     * Make the initial Product Query
     */
    const { loading, error, data, refetch } = useQuery<bundleProductDetail, bundleProductDetailVariables>(
        bundleProductQuery,
        {
            variables: { ids: [props.id], noAuthorization: true },
            ssr: useDeps().env.SSR_GQL,
        },
    );

    const customisationOption = useBundleCustomisationData(data?.['bundleProductDetail']?.[0]?.logo_policy?.toString());

    /**
     * if the currency changes, re-fire the query for the product
     */
    useCurrencyChange(refetch);

    /**
     * Loading handler
     */
    if (loading)
        return (
            <Wrapper variant="page-inner">
                <Container>
                    <BundlePageSkeleton />
                </Container>
            </Wrapper>
        );

    /**
     * Error handling
     */
    if (error) {
        return <ErrorComponent message={error?.message} />;
    }
    if (!data) {
        Bugsnag.notify('returning 404 as the data was absent');
        return <NotFound />;
    }

    if (!Array.isArray(data.bundleProductDetail)) {
        Bugsnag.notify('returning 404 as the data was absent');

        return <NotFound />;
    }
    const first = data.bundleProductDetail[0];

    if (!first) {
        Bugsnag.notify("returning 404 as the 'first' data was absent");
        return <NotFound />;
    }

    /**
     * Clean up of product data
     */
    const product = cleanBundleProduct(first);

    return <BundleProduct product={product} customisationOption={customisationOption} />;
};

const BundleProduct: React.FC<BundleProductProps> = ({ product, customisationOption }) => {
    const { DOMAIN } = useDeps().env;
    useSetPopupPageProduct(product.id, product.categories?.[0]?.id ?? null);

    const [reMountKey, reMount] = useState(0);
    const reMountForm = () => reMount(reMountKey + 1);

    const bundleInitialValues: FormInitialValues = {
        color: 'blue',
        font: 'sans',
        textLineOne: '',
        textLineTwo: '',
        textLineThree: '',
        customisationName: '',
        image: '',
        isContactRequested: 'upload',
        specialInstructions: '',
    };

    const url = currentUrl(DOMAIN!, `/${product.url_key}` || '');

    const bundlePrice = useBundleOptionsPrice(product.id);
    useKlaviyoViewedBundle({ product, price: bundlePrice, url });

    const Component = ModalBundleProductPage;

    const productName = product.name;
    const title = productName.toLowerCase().includes('workwear express')
        ? productName
        : `${productName} | Workwear Express`;

    const noIndexedProduct = useIndexSku(product.sku);
    const { setHead } = useHeadTags();

    useEffect(() => {
        const desc = product.meta_description || product.meta_title || product.name || '';
        const meta = [
            `<meta name="description" content="${escapeAttr(desc)}" />`,
            noIndexedProduct ? '<meta name="robots" content="noindex" />' : '',
            `<meta property="og:title" content="${escapeAttr(product.name || '')}" />`,
            `<meta property="og:description" content="${escapeAttr(desc)}" />`,
            `<meta property="og:image" content="${escapeAttr(absoluteUrl(DOMAIN!, product.small_image || ''))}" />`,
            `<meta name="keywords" content="${escapeAttr(product.meta_keywords || KEYWORDS)}" />`,
        ].join('');
        const link = `<link rel="canonical" href="${escapeAttr(url)}" />`;
        setHead({ title: `<title>${escapeAttr(title)}</title>`, meta, link });
    }, [title, product, url, noIndexedProduct, setHead, DOMAIN]);

    return (
        <Component
            key={reMountKey}
            reMountForm={reMountForm}
            initialValues={bundleInitialValues}
            product={product}
            customisationOption={customisationOption}
        />
    );
};

export default BundleProductRoot;
